import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import CartButton from './CartButton';
import { supabase } from '../../lib/supabaseClient';

export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    // Check auth status
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    checkAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/treatments', label: 'Treatments' },
    { path: '/membership', label: 'Membership' },
    { path: '/book', label: 'Contact' },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
          isScrolled ? 'bg-white/97 backdrop-blur-md shadow-sm border-b border-[#e8e2da]/60' : 'bg-transparent'
        }`}
      >
        <div className="px-8 lg:px-16">
          <div className="flex items-center justify-between h-20">

            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img
                src="https://public.readdy.ai/ai/img_res/51863d86-6e89-41cb-8d37-6b29fa197d2b.png"
                alt="IV Hydration Club"
                className="h-10 w-auto"
              />
            </Link>

            {/* Desktop Nav Links */}
            <div className="hidden md:flex items-center gap-10">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-[11px] font-light tracking-[0.2em] uppercase transition-colors duration-300 ${
                    location.pathname === link.path
                      ? 'text-charcoal'
                      : 'text-greige hover:text-charcoal'
                  }`}
                >
                  {link.label}
                  {location.pathname === link.path && (
                    <span className="block h-px bg-gold mt-1 w-full"></span>
                  )}
                </Link>
              ))}
              <Link
                to="/faq"
                className={`text-[11px] font-light tracking-[0.2em] uppercase transition-colors duration-300 ${
                  location.pathname === '/faq'
                    ? 'text-charcoal'
                    : 'text-greige hover:text-charcoal'
                }`}
              >
                FAQ
              </Link>
              <CartButton />
              {user ? (
                <Link
                  to="/dashboard"
                  className="w-8 h-8 flex items-center justify-center bg-gold/10 rounded-full hover:bg-gold/20 transition-colors cursor-pointer"
                  title="My Account"
                >
                  <i className="ri-user-line text-gold"></i>
                </Link>
              ) : (
                <Link
                  to="/login"
                  className="text-[11px] font-light tracking-[0.2em] uppercase text-greige hover:text-charcoal transition-colors duration-300 cursor-pointer"
                >
                  Sign In
                </Link>
              )}
            </div>

            {/* Book Now CTA */}
            <Link
              to="/book"
              className="hidden md:block px-7 py-2.5 bg-charcoal text-white text-[11px] font-light tracking-[0.2em] uppercase hover:bg-charcoal/80 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Book Now
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden text-charcoal cursor-pointer"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-white flex flex-col items-center justify-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className="text-sm font-light tracking-[0.25em] uppercase text-charcoal hover:text-gold transition-colors duration-300 cursor-pointer"
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/faq"
            className="text-sm font-light tracking-[0.25em] uppercase text-charcoal hover:text-gold transition-colors duration-300 cursor-pointer"
          >
            FAQ
          </Link>
          {user ? (
            <Link
              to="/dashboard"
              className="text-sm font-light tracking-[0.25em] uppercase text-charcoal hover:text-gold transition-colors duration-300 cursor-pointer"
            >
              My Account
            </Link>
          ) : (
            <Link
              to="/login"
              className="text-sm font-light tracking-[0.25em] uppercase text-charcoal hover:text-gold transition-colors duration-300 cursor-pointer"
            >
              Sign In
            </Link>
          )}
          <Link
            to="/book"
            className="mt-4 px-10 py-3 bg-charcoal text-white text-[11px] font-light tracking-[0.2em] uppercase whitespace-nowrap cursor-pointer"
          >
            Book Now
          </Link>
          <div className="mt-4">
            <CartButton />
          </div>
        </div>
      )}
    </>
  );
}